import { type KeyboardEvent, useCallback } from 'react'

import { Icon, Space, WithEllipsisTooltip } from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { CatalogItemTag, type ICatalogItemTagProps } from '../CatalogItemTag'
import { CatalogItemBodyStyled } from './styles/CatalogItemBodyStyled'
import { CatalogItemContentStyled } from './styles/CatalogItemContentStyled'
import { CatalogItemImageStyled } from './styles/CatalogItemImageStyled'
import { CatalogItemImageWrapperStyled } from './styles/CatalogItemImageWrapperStyled'
import { CatalogItemLayoutStyled } from './styles/CatalogItemLayoutStyled'
import { CatalogItemPriceStyled } from './styles/CatalogItemPriceStyled'
import { CatalogItemTitleStyled } from './styles/CatalogItemTitleStyled'

export interface ICatalogItemProps {
  img?: string
  title: string
  body: string
  tags: ICatalogItemTagProps[]
  price: number
  currency: CurrencyName
  isSelected?: boolean
  onClick: () => void
}

export const CatalogItem = ({
  img,
  title,
  body,
  tags,
  price,
  currency,
  isSelected = false,
  onClick,
}: ICatalogItemProps) => {
  const formatMedia = useFormatMedia()

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    },
    [onClick]
  )

  const renderImage = () => {
    if (img) return <CatalogItemImageStyled src={formatMedia(img)} alt='' />
    return <Icon icon='gallery' size={24} color='darkGrey' aria-hidden />
  }

  const renderTags = () => {
    if (tags.length === 0) return null
    return (
      <Space direction='horizontal' size='small' flexWrap>
        {tags.map((tag, index) => (
          <CatalogItemTag
            key={`${index}-${tag.text}`}
            icon={tag.icon}
            text={tag.text}
            variant={tag.variant}
          />
        ))}
      </Space>
    )
  }

  return (
    <CatalogItemLayoutStyled
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role='button'
      tabIndex={0}
      aria-pressed={isSelected}
      $isSelected={isSelected}
    >
      <CatalogItemImageWrapperStyled>
        {renderImage()}
      </CatalogItemImageWrapperStyled>
      <CatalogItemContentStyled>
        <Space direction='vertical' size='xsmall'>
          <Space
            direction='horizontal'
            justify='space-between'
            align='baseline'
            size='medium'
          >
            <Space.Item grow={1} minWidth={0}>
              <WithEllipsisTooltip title={title}>
                <CatalogItemTitleStyled noWrap textEllipsis className='fs-mask'>
                  {title}
                </CatalogItemTitleStyled>
              </WithEllipsisTooltip>
            </Space.Item>
            <CatalogItemPriceStyled className='fs-mask'>
              <Price inline price={price} currency={currency} />
            </CatalogItemPriceStyled>
          </Space>
          <CatalogItemBodyStyled className='fs-mask'>
            {body}
          </CatalogItemBodyStyled>
        </Space>
        {renderTags()}
      </CatalogItemContentStyled>
    </CatalogItemLayoutStyled>
  )
}
